import { Star, StarBorderOutlined } from "@mui/icons-material"
import {
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { yellow } from "@mui/material/colors"
import { useStore } from "@nanostores/react"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"

import type { Pool } from "../../api/pools-api"
import { PercentChange } from "../../components/PercentChange"
import { TokenAvatar } from "../../components/TokenAvatar"
import { $userSettings } from "../../stores/user-settings-store"
import { useTokenPrices } from "@/api/portfolio-api"
import MarketCapDisplay from "@/components/MarketCapDisplay"
import { PoolWithFee } from "@/components/PoolWithFee"
import { PriceDisplay } from "@/components/PriceDisplay"
import type { PoolOverview } from "@/types"
import { sortPoolAssets } from "@/utils/flipper"

type PoolsTableRowProps = {
  row: Pool[]
  overviewMap: Record<string, PoolOverview>
}

export function getPoolGroupId(pool: Pool) {
  const { token0, token1 } = pool

  if (token0 < token1) {
    return `${token0}__${token1}`
  }

  return `${token1}__${token0}`
}

export function PoolsTableRow(props: PoolsTableRowProps) {
  const { row, overviewMap } = props
  const { id } = row[0]

  const overview = overviewMap[id]

  const sortedTokens = sortPoolAssets({
    token0: overview.token0,
    token1: overview.token1,
    token0Ticker: overview.token0Ticker,
    token1Ticker: overview.token1Ticker,
  })

  const isFlipped = false

  const baseToken = sortedTokens.base
  const quoteToken = sortedTokens.token
  const baseTokenTicker =
    sortedTokens.base === overview.token0 ? overview.token0Ticker : overview.token1Ticker
  const quoteTokenTicker =
    sortedTokens.token === overview.token0 ? overview.token0Ticker : overview.token1Ticker

  const userSettings = useStore($userSettings)
  const isOnWatchlist =
    !!userSettings && !!userSettings.watchlist.find((x) => x === getPoolGroupId(row[0]))

  const navigate = useNavigate()

  const [prices] = useTokenPrices()
  const tokenPrice = prices?.[baseToken] ? prices[baseToken].price || 0 : 0

  // console.log("PoolsTableRow", row, overview)

  const price5mAgo = overview.token1Price5mAgo
  const price1hAgo = overview.token1Price1hAgo
  const price6hAgo = overview.token1Price6hAgo
  const price24hAgo = overview.token1Price24hAgo

  const currentPrice = overview.token1CurrentPrice

  const liquidityUsd = useMemo(() => {
    return row.reduce((acc, pool) => acc + overviewMap[pool.id].liquidityUsd, 0)
  }, [row])
  const volumeUsd = useMemo(() => {
    return row.reduce((acc, pool) => acc + overviewMap[pool.id].volumeUsd, 0)
  }, [row])
  const transactions = useMemo(() => {
    return row.reduce((acc, pool) => acc + overviewMap[pool.id].transactions, 0)
  }, [row])

  const watchlistHandler = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!userSettings) {
      document.querySelector<HTMLButtonElement>("#connect-wallet-button")?.click()
      return
    }
    const updated = { ...userSettings }
    const newVal = !isOnWatchlist
    updated.watchlist = newVal
      ? [...updated.watchlist, getPoolGroupId(row[0])]
      : updated.watchlist.filter((x) => x !== getPoolGroupId(row[0]))
    $userSettings.set(updated)
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))

  if (isMobile) {
    return (
      <TableRow hover onClick={() => navigate(`/pool/${id}`)} sx={{ cursor: "pointer" }}>
        <TableCell colSpan={11}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={"space-between"}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip
                  placement="bottom"
                  title={!userSettings ? "Connect wallet to use the watchlist" : ""}
                >
                  <span>
                    <IconButton
                      disabled={userSettings === undefined}
                      onClick={watchlistHandler}
                      sx={{ color: isOnWatchlist ? yellow[600] : "var(--mui-palette-grey-700)" }}
                    >
                      {typeof userSettings === "undefined" ? (
                        <Skeleton width={24} height={24} />
                      ) : isOnWatchlist ? (
                        <Star />
                      ) : (
                        <StarBorderOutlined />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <TokenAvatar tokenId={baseToken} />
                <TokenAvatar tokenId={quoteToken} />
                <Typography fontWeight={500} variant="body2">
                  {baseTokenTicker}/{quoteTokenTicker}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  display: "inline-flex",
                  "& > div > a": {
                    padding: 0.5,
                  },
                  "&  svg": {
                    width: 14,
                    height: 14,
                  },
                }}
              >
                {row.map((pool) => (
                  <Tooltip
                    title={
                      <>
                        Liquidity <PriceDisplay amount={overviewMap[pool.id].liquidityUsd} />
                      </>
                    }
                  >
                    <div>
                      <PoolWithFee pool={pool} />
                    </div>
                  </Tooltip>
                ))}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Typography variant="body2">
                Price: <PriceDisplay amount={tokenPrice || 0} />
              </Typography>
              <Typography variant="body2">
                Market Cap: <MarketCapDisplay pool={overview} />
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Typography variant="body2">
                5m: <PercentChange latestPrice={currentPrice} previousPrice={price5mAgo} />
              </Typography>
              <Typography variant="body2">
                1h: <PercentChange latestPrice={currentPrice} previousPrice={price1hAgo} />
              </Typography>
              <Typography variant="body2">
                6h: <PercentChange latestPrice={currentPrice} previousPrice={price6hAgo} />
              </Typography>
              <Typography variant="body2">
                24h: <PercentChange latestPrice={currentPrice} previousPrice={price24hAgo} />
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Typography variant="body2">
                Liquidity: <PriceDisplay amount={liquidityUsd} />
              </Typography>
              <Typography variant="body2">
                Volume: <PriceDisplay amount={volumeUsd} />
              </Typography>
              <Typography variant="body2">Tx: {transactions}</Typography>
            </Stack>
          </Stack>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <TableRow
        sx={{ cursor: "pointer" }}
        onClick={() => {
          navigate(`/pool/${id}`)
        }}
        hover
      >
        <TableCell sx={{ paddingRight: 0 }}>
          <Tooltip
            placement="bottom"
            title={!userSettings ? "Connect wallet to use the watchlist" : ""}
          >
            <span>
              <IconButton
                size="small"
                disabled={userSettings === undefined}
                onClick={watchlistHandler}
                sx={{ color: isOnWatchlist ? yellow[600] : "var(--mui-palette-grey-700)" }}
              >
                {typeof userSettings === "undefined" ? (
                  <Skeleton width={20} height={20} />
                ) : isOnWatchlist ? (
                  <Star fontSize="small" />
                ) : (
                  <StarBorderOutlined fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>

        <TableCell variant="text">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TokenAvatar tokenId={baseToken} />
            <TokenAvatar tokenId={quoteToken} />
            <span>
              <Typography component="span" variant="inherit" fontWeight={500}>
                {baseTokenTicker}
              </Typography>
              <Typography
                component="span"
                color="text.secondary"
                variant="inherit"
                fontWeight={300}
              >
                /{quoteTokenTicker}
              </Typography>
            </span>
          </Stack>
        </TableCell>

        <TableCell variant="number">
          <Stack direction="row" spacing={-0.5}>
            {row.map((pool) => (
              <Tooltip
                title={
                  <>
                    Liquidity <PriceDisplay amount={overviewMap[pool.id].liquidityUsd} />
                  </>
                }
              >
                <div>
                  <PoolWithFee pool={pool} />
                </div>
              </Tooltip>
            ))}
          </Stack>
        </TableCell>

        <TableCell align="right" variant="number">
          <PriceDisplay amount={tokenPrice || 0} />
        </TableCell>

        <TableCell align="right" variant="number">
          {transactions}
        </TableCell>

        <TableCell align="right" variant="number">
          <PriceDisplay amount={volumeUsd} />
        </TableCell>

        <TableCell variant="number">
          <PercentChange latestPrice={currentPrice} previousPrice={price5mAgo} />
        </TableCell>

        <TableCell variant="number">
          <PercentChange latestPrice={currentPrice} previousPrice={price1hAgo} />
        </TableCell>

        <TableCell variant="number">
          <PercentChange latestPrice={currentPrice} previousPrice={price6hAgo} />
        </TableCell>

        <TableCell variant="number">
          <PercentChange latestPrice={currentPrice} previousPrice={price24hAgo} />
        </TableCell>

        <TableCell align="right" variant="number">
          <PriceDisplay amount={liquidityUsd} />
        </TableCell>

        <TableCell align="right" variant="number">
          <MarketCapDisplay pool={overview} />
        </TableCell>
      </TableRow>
    </>
  )
}
